import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { SettingsSectionWrapper } from "~/components/settings/SettingsSection";
import { formatAutostoreBinTypeDisplay } from "~/features/preview/bins/binSettings.utils";
import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import {
  useAvailableBinReportQuery,
  useGetAutostoreGridQuery,
  useGetBinCleaningReportQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

export function BinsOverviewSection() {
  const { t } = useTranslation();
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );

  const { data: binCleaningReport } = useGetBinCleaningReportQuery(
    selectedAutostoreGridId!,
    { skip: !selectedAutostoreGridId }
  );
  const { data: selectedGrid } = useGetAutostoreGridQuery(
    selectedAutostoreGridId!,
    { skip: !selectedAutostoreGridId }
  );
  const { data: availableBinReport } = useAvailableBinReportQuery(
    selectedAutostoreGridId!,
    { skip: !selectedAutostoreGridId }
  );

  const { totalBins } = binCleaningReport ?? {};
  const formattedTotalBins =
    typeof totalBins === "number" ? totalBins.toLocaleString("en-US") : "";

  const availableWholeBins = availableBinReport?.find(
    (row) => row.numberOfCompartments === 1
  )?.binsAvailable;
  const availableHalfBins = availableBinReport?.find(
    (row) => row.numberOfCompartments === 2
  )?.binsAvailable;
  const availableQuarterBins = availableBinReport?.find(
    (row) => row.numberOfCompartments === 4
  )?.binsAvailable;

  const formatCount = (value: number | undefined) =>
    typeof value === "number" ? value.toLocaleString("en-US") : "";

  return (
    <SettingsSectionWrapper title={t("overview")}>
      <Stack spacing={2.5} sx={{ width: "100%" }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={8}>
          <OverviewFieldRow
            label={t("grid bin count")}
            helperText={t("total # of bins in grid")}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formattedTotalBins}
            </Typography>
          </OverviewFieldRow>

          <OverviewFieldRow label={t("available bin count")}>
            <Stack direction="row" gap={3} flexWrap="wrap">
              <Stack spacing={0.5}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCount(availableWholeBins)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("whole")}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCount(availableHalfBins)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("half")}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCount(availableQuarterBins)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("quarter")}
                </Typography>
              </Stack>
            </Stack>
          </OverviewFieldRow>
        </Stack>

        <OverviewFieldRow
          label={t("bin type")}
          helperText={t("the size of a bin in the grid")}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatAutostoreBinTypeDisplay(selectedGrid?.autostoreBinType)}
          </Typography>
        </OverviewFieldRow>
      </Stack>
    </SettingsSectionWrapper>
  );
}
